import React from 'react';
import Link from 'next/link';
import { InstagramIcon, FacebookIcon, PinterestIcon, RedditIcon, ShieldCheckIcon, MailIcon, GlobeIcon } from './Icons';
import type { ToolId } from './utils/slugs';
import { toolIdToSlug } from './utils/slugs';

interface FooterProps {
    setActiveTool: (toolId: ToolId) => void;
}

export const Footer: React.FC<FooterProps> = ({ setActiveTool }) => {
    const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, toolId: ToolId) => {
        e.preventDefault();
        setActiveTool(toolId);
    }

    return (
        <footer className="mt-24 border-t border-stone-100 bg-stone-50/50 py-16 px-6">
            <div className="max-w-7xl mx-auto">
                {/* Trust Signals & Contact */}
                <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12 mb-12 border-b border-stone-100 pb-12">
                    <div className="flex items-center gap-2 text-stone-600 font-bold text-xs uppercase tracking-widest">
                        <ShieldCheckIcon className="w-5 h-5 text-brand-pink" />
                        Secure Checkout
                    </div>
                    <div className="flex items-center gap-2 text-stone-600 font-bold text-xs uppercase tracking-widest">
                        <MailIcon className="w-5 h-5 text-brand-pink" />
                        Email Support
                    </div>
                    <div className="flex items-center gap-2 text-stone-600 font-bold text-xs uppercase tracking-widest">
                        <GlobeIcon className="w-5 h-5 text-brand-pink" />
                        Worldwide Digital Delivery
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 text-center md:text-left mb-12">
                    <div>
                        <Link href="/" className="font-serif text-2xl font-bold text-stone-900 tracking-tight">
                            Sweety's <span className="text-brand-pink">Bakery</span>
                        </Link>
                        <p className="mt-4 text-sm text-brand-text-body/70 leading-relaxed max-w-sm mx-auto md:mx-0">
                            Empowering home bakers and cottage food businesses with professional-grade tools for pricing, scaling, and managing their passion.
                        </p>
                    </div>

                    <div className="flex flex-col gap-3">
                        <h4 className="font-bold text-stone-900 uppercase tracking-widest text-xs mb-2">Legal & Support</h4>
                        <Link href={toolIdToSlug('privacy-policy')} className="text-sm text-brand-text-body/70 hover:text-brand-pink transition-colors w-max mx-auto md:mx-0">Privacy Policy</Link>
                        <Link href={toolIdToSlug('terms-of-use')} className="text-sm text-brand-text-body/70 hover:text-brand-pink transition-colors w-max mx-auto md:mx-0">Terms of Service</Link>
                        <Link href="/refund-policy" className="text-sm text-brand-text-body/70 hover:text-brand-pink transition-colors w-max mx-auto md:mx-0">Refund Policy</Link>
                        <Link href={toolIdToSlug('contact-us')} className="text-sm text-brand-text-body/70 hover:text-brand-pink transition-colors w-max mx-auto md:mx-0">Contact Us</Link>
                    </div>

                    <div>
                        <h4 className="font-bold text-stone-900 uppercase tracking-widest text-xs mb-4">Connect With Us</h4>
                        <div className="flex justify-center md:justify-start gap-4 mb-6">
                            <a href="https://www.instagram.com/sweetys.bakery/" target="_blank" rel="noopener noreferrer" className="p-2 bg-white border border-stone-100 rounded-lg text-stone-400 hover:text-brand-pink hover:border-brand-pink/20 transition-all shadow-sm" aria-label="Follow us on Instagram"><InstagramIcon className="w-5 h-5" /></a>
                            <a href="https://www.pinterest.com/Sweetysbakerys/" target="_blank" rel="noopener noreferrer" className="p-2 bg-white border border-stone-100 rounded-lg text-stone-400 hover:text-brand-pink hover:border-brand-pink/20 transition-all shadow-sm" aria-label="Follow us on Pinterest"><PinterestIcon className="w-5 h-5" /></a>
                            <a href="https://web.facebook.com/SaPatissl/" target="_blank" rel="noopener noreferrer" className="p-2 bg-white border border-stone-100 rounded-lg text-stone-400 hover:text-brand-pink hover:border-brand-pink/20 transition-all shadow-sm" aria-label="Follow us on Facebook"><FacebookIcon className="w-5 h-5" /></a>
                        </div>
                        <address className="not-italic text-sm text-brand-text-body/70 space-y-1">
                            <p className="flex items-center justify-center md:justify-start gap-2">📍 Marrakech, Morocco</p>
                            <p className="flex items-center justify-center md:justify-start gap-2">📧 hello@sweetysbakery.com</p>
                        </address>
                    </div>
                </div>

                <div className="pt-8 border-t border-stone-100 text-center">
                    <p className="text-xs text-stone-400">&copy; {new Date().getFullYear()} Sweety's Bakery. Built for Bakers with ❤️</p>
                </div>
            </div>
        </footer>
    );
};